import { useState, useEffect, useMemo, SyntheticEvent } from "react";
import { Autocomplete, TextField, Box, IconButton, CircularProgress } from "@mui/material";
import { Save, Close } from "@mui/icons-material";
import {
  useRecordContext,
  useUpdate,
  useGetList,
  useGetOne,
  useRefresh,
  ReferenceField,
  TextField as RaTextField,
  FunctionField,
  Labeled,
  useResourceContext,
  Validator,
} from "react-admin";
import { ResourceName, ValidAny } from "@/types";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";

interface InPlaceReferenceEditorProps {
  source: string;
  displayField?: string;
  reference?: ResourceName;
  label?: string;
  helperText?: string;
  showButtons?: boolean;
  filter?: object;
  readOnly?: boolean;
  /** Custom label function for autocomplete options. Falls back to displayField. */
  getOptionLabel?: (option: ValidAny) => string;
  validate?: Validator | Validator[];
}

/**
 * In-place editable reference field with search/autocomplete functionality.
 *
 * In display mode, renders a {@link ReferenceField} that uses a foreign key (`source`) to look up
 * a related resource (`reference`), and displays a field (`displayField`) from that record.
 *
 * In edit mode, shows an Autocomplete dropdown with search functionality for finding options
 * from the reference resource. Users can type to filter results server-side.
 *
 * @param source - Foreign key in the current record (e.g. "client_id_ref")
 * @param reference - Resource name to reference (e.g. ResourceName.CLIENTS, ResourceName.VENDORS)
 * @param displayField - Field from the referenced resource to display (e.g. "client_legal_name", "company_name")
 * @param label - Optional label to display
 * @param helperText - Optional helper text
 * @param showButtons - Whether to show edit/save/cancel buttons
 * @param filter - Filter by a specific id ref (e.g. "client_id_ref")
 * @param readOnly - If true, disables editing and shows display only
 */
const InPlaceReferenceEditor = ({
  source,
  reference = ResourceName.VENDORS,
  displayField = "",
  label,
  helperText,
  showButtons = true,
  filter,
  readOnly = false,
  getOptionLabel: getOptionLabelProp,
  validate,
}: InPlaceReferenceEditorProps) => {
  const record = useRecordContext();
  const resource = useResourceContext();
  const [isEditing, setIsEditing] = useState(false);
  const [selectedValue, setSelectedValue] = useState<ValidAny>(null);
  const [searchText, setSearchText] = useState("");
  const [validationError, setValidationError] = useState<string | undefined>();
  const [update, { isLoading: isUpdating }] = useUpdate();
  const notifyError = useRichApiErrorNotify();
  const refresh = useRefresh();

  /** Run validate prop against a value, return first error or undefined */
  const runValidation = (value: ValidAny): string | undefined => {
    if (!validate) return undefined;
    const validators = Array.isArray(validate) ? validate : [validate];
    for (const v of validators) {
      const result = v(value, {}, {});
      if (typeof result === "string") return result;
      if (result && typeof result === "object" && "message" in result) return result.message;
    }
    return undefined;
  };

  // Get current value from record
  const currentReferenceId = record?.[source];

  // Fetch the currently selected reference to display it initially
  const { data: currentReference } = useGetOne(
    reference,
    { id: currentReferenceId },
    { enabled: !!currentReferenceId && isEditing }
  );

  // Merge custom filter with search filter so both apply
  const combinedFilter = useMemo(() => {
    const base = filter || {};
    if (searchText) return { ...base, q: searchText };
    return base;
  }, [filter, searchText]);

  // Fetch filtered/searched reference data for the dropdown
  const {
    data: referenceData = [],
    isLoading: isLoadingOptions,
    error: referenceError,
  } = useGetList(
    reference,
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: displayField, order: "ASC" },
      filter: combinedFilter,
    },
    { enabled: isEditing }
  );

  // Combine current reference with search results to ensure selected value is always available
  const options = useMemo(() => {
    if (!isEditing) return [];

    const allOptions = referenceData;

    // Add current reference if it's not already in the list
    if (currentReference && !allOptions.find((opt) => opt.id === currentReference.id)) {
      allOptions.unshift(currentReference);
    }

    return allOptions;
  }, [referenceData, currentReference, isEditing]);

  useEffect(() => {
    if (currentReference && isEditing) {
      setSelectedValue(currentReference);
    } else if (isEditing && !currentReferenceId) {
      setSelectedValue(null);
    }
  }, [currentReference, isEditing, currentReferenceId]);

  const handleEdit = () => {
    if (readOnly) return;
    setIsEditing(true);
    setSearchText("");
  };

  const handleSave = async () => {
    if (!resource) return;

    const error = runValidation(selectedValue?.id || null);
    if (error) {
      setValidationError(error);
      return;
    }

    try {
      await update(
        resource,
        {
          id: record?.id,
          data: { ...record, [source]: selectedValue?.id || null },
          previousData: record,
        },
        {
          onSuccess: () => {
            setIsEditing(false);
            setSearchText("");
            refresh();
          },
          onError: (error) => {
            notifyError(error);
            setSelectedValue(currentReference || null);
          },
        }
      );
    } catch (error) {
      console.error("Update failed:", error);
      setSelectedValue(currentReference || null);
    }
  };

  const handleCancel = () => {
    setSelectedValue(currentReference || null);
    setIsEditing(false);
    setSearchText("");
  };

  const handleValueChange = (_event: SyntheticEvent, newValue: ValidAny) => {
    setSelectedValue(newValue);
    setValidationError(undefined);
  };

  const handleInputChange = (_event: SyntheticEvent, newInputValue: string) => {
    setSearchText(newInputValue);
  };

  if (referenceError) {
    console.error("Reference error:", referenceError);
    return (
      <Labeled label={label}>
        <TextField value="Error loading options" error helperText={helperText} disabled fullWidth size="small" />
      </Labeled>
    );
  }

  // Sub-type references (e.g. "vendors/carriers") are data-provider keys, not routes.
  // The show page lives on the base resource (e.g. "vendors"), so link there — the
  // default ReferenceField link would point at the unrouted sub-type and dead-end.
  const showResource = reference.split("/")[0];
  const referenceLink = (linked: ValidAny) => `/${showResource}/${linked.id}/show`;

  const referenceDisplayField = getOptionLabelProp ? (
    <FunctionField render={(record: ValidAny) => getOptionLabelProp(record) || "Not set"} />
  ) : (
    <RaTextField source={displayField} emptyText="Not set" />
  );

  // Read-only mode: just show the reference field without edit button
  if (readOnly) {
    return (
      <Box sx={{ position: "relative", display: "inline-block", width: "100%" }}>
        <Labeled label={label}>
          <ReferenceField source={source} reference={reference} link={referenceLink} sx={{ display: "block", mb: 0.5 }}>
            {referenceDisplayField}
          </ReferenceField>
        </Labeled>
      </Box>
    );
  }

  // Display mode - click anywhere to edit
  if (!isEditing) {
    return (
      <Box
        onClick={handleEdit}
        sx={{
          display: "inline-block",
          width: "100%",
          cursor: "pointer",
        }}
      >
        <Labeled label={label}>
          <ReferenceField source={source} reference={reference} link={referenceLink} sx={{ display: "block", mb: 0.5 }}>
            {referenceDisplayField}
          </ReferenceField>
        </Labeled>
      </Box>
    );
  }

  // Edit mode with search/autocomplete
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
      <Autocomplete
        value={selectedValue}
        onChange={handleValueChange}
        onInputChange={handleInputChange}
        options={options}
        getOptionLabel={(option) => getOptionLabelProp?.(option) || option?.[displayField] || ""}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        loading={isLoadingOptions}
        noOptionsText={searchText ? "No matches found. Try a different search." : "Start typing to search..."}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            size="small"
            placeholder="Type to search..."
            error={!!validationError}
            helperText={validationError}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoadingOptions ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.id}>
            {getOptionLabelProp?.(option) || option[displayField]}
          </Box>
        )}
        fullWidth
        size="small"
        disabled={isUpdating}
        clearOnEscape
      />

      {showButtons && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <IconButton size="small" onClick={handleSave} disabled={isUpdating || isLoadingOptions}>
            {isUpdating ? <CircularProgress size={16} /> : <Save fontSize="small" />}
          </IconButton>

          <IconButton size="small" onClick={handleCancel} disabled={isUpdating}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

export default InPlaceReferenceEditor;
